import { ChangeDetectionStrategy, Component, effect, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { AuthenticatedPageComponent } from 'src/app/components/base.component';
import { MainpageComponent } from 'src/app/components/mainpage/mainpage.component';
import { GenericModalComponent } from 'src/app/components/ui/generic-modal/generic-modal.component';
import { Shop, ShopItem } from 'src/app/dto';
import { ApplicationService } from 'src/app/services/application.service';
import { ShopService } from 'src/app/services/shop.service';
import { InventoryItemOptionsComponent } from '../../inventory-item-options/inventory-item-options.component';

@Component({
    selector: 'app-shop-view',
    templateUrl: './shop-view.component.html',
    styleUrls: ['./shop-view.component.scss'],
    host: { class: 'full-width' },
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ShopViewComponent extends AuthenticatedPageComponent {
    public static route = 'shop';

    shopId = input<string>();
    // Backed by a signal, not a plain field - every write happens inside the effect()/subscribe
    // callbacks below, none of which are template-bound handlers. equal: () => false because
    // fetchShopstock() mutates the held Shop's .Items in place (it can be the exact cached
    // instance ShopService.activeUserShops holds, see that method's own comment) rather than
    // replacing it, so every .set() - even of the same reference - must be treated as a change.
    // The getter keeps every shop.X read in the template working unchanged.
    private shopState = signal<Shop>(new Shop(null), { equal: () => false });
    // Also reads ShopService.stockRevision so a template reading shop.Items re-renders when an item
    // is spliced out of the held Shop's Items (a purchase, or a "sold out" rejection).
    get shop(): Shop {
        this.shopService.stockRevision();
        return this.shopState();
    }
    // Same shape as shop above - both writes happen inside the effect()/subscribe below, never
    // from a template-bound handler.
    protected isMe = signal(false);

    constructor(private shopService: ShopService, protected coreAppService: ApplicationService, router: Router) {
        super(router, coreAppService.authService, MainpageComponent.route, coreAppService.uiStateService);
        effect((onCleanup) => {
            // ShopService's fetch methods are typed number - route params always arrive as
            // strings, so this must be parsed before use (previously the raw string was assigned
            // into a number-typed field without conversion, so the strict === below against a
            // real Shop.Id number silently never matched and this fast path was dead).
            const shopId = Number(this.shopId());

            const myShop = this.shopService.activeUserShopsSignal().filter(s => s.Id === shopId);
            if (myShop && myShop.length > 0) {
                this.shopState.set(myShop[0]);
                this.isMe.set(true);
                const stockSub = this.fetchShopstock(shopId);
                onCleanup(() => stockSub.unsubscribe());
            } else {
                const shopSub = this.shopService.getShop(shopId).subscribe(s => {
                    this.shopState.set(s);
                    //make sure this wasnt a race condition and we somehow hadn't loaded activeUserShops
                    const activeUser = this.activeUser();
                    if (activeUser && this.shop.OwnerUsername) {
                        this.isMe.set(activeUser.Username === this.shop.OwnerUsername);
                    }
                    const stockSub = this.fetchShopstock(shopId);
                    onCleanup(() => stockSub.unsubscribe());
                });
                onCleanup(() => shopSub.unsubscribe());
            }
        });
    }

    displayItemOptions(item: ShopItem) {
        this.coreAppService.uiStateService.openModalWithComponent(
            { size: 'lg', },
            this.shop.Name,
            item.ItemType.Name,
            InventoryItemOptionsComponent,
            GenericModalComponent,
            (invOptions: InventoryItemOptionsComponent, openedModal: NgbActiveModal) => { invOptions.item = item; invOptions.containingShop = this.shop; }
        );

    }


    // Assignment, not accumulation: getShopItems always returns the shop's full current stock, and
    // this.shop can be the same cached Shop instance activeUserShops holds (the isMe branch above) -
    // pushing onto it would duplicate items on every re-run this effect sees for the same shop (e.g.
    // navigating away and back to it under this app's route reuse strategy).
    private fetchShopstock(shopId: number): Subscription {
        return this.shopService.getShopItems(shopId).subscribe(stock => {
            this.shop.Items = stock;
            this.shopState.set(this.shop); // re-notify - same reference, equal: () => false forces it through
        });
    }
}
